import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ContractKeySchema,
  type ContractDetail,
  type ContractLink,
  type ContractLinkCandidate,
  type ContractLinkEffect,
  type ContractLinkRefusal,
  type ContractLinksResponse,
  type SetContractLinkResponse,
} from '@clm/shared-types';

import { fetchContractLinks, setContractLink } from '../api/contract-links';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { ErrorNotice } from '../components/ErrorNotice';
import { refusalCodeOf } from './base-msa-shared';

/** The roles the gateway grants `admin:configure` to — a display hint, never the gate (R-120-1). */
const LINK_EDITOR_ROLES: readonly string[] = ['admin'];

/** The four things the dialog can do (R-120-2). Removing a parent names no target. */
type LinkAction = 'child_of_set' | 'child_of_remove' | 'amends' | 'supersedes';

const LINK_OF: Readonly<Record<LinkAction, ContractLink>> = Object.freeze({
  child_of_set: 'child_of',
  child_of_remove: 'child_of',
  amends: 'amends',
  supersedes: 'supersedes',
});

/** Why the server withholds an action or refuses a link, in words — keyed on its code, never re-derived here. */
export const LINK_REFUSAL_COPY: Readonly<Record<ContractLinkRefusal, string>> = Object.freeze({
  contract_not_found: 'A contract this would change is not visible to you.',
  stale_view: 'The links changed since this opened — reloaded; nothing was written.',
  superseded_read_only: 'It is Superseded and read-only; its replacement carries the live links.',
  no_change: 'Nothing would change.',
  amendment_follows_agreement: 'It amends an agreement and sits where its agreement sits — change the agreement’s links instead.',
  supersession_is_one_way: 'It already replaces another agreement; a replacement is never undone or moved.',
  relationship_not_valid: 'The upload’s rules do not allow this link.',
  party_follows_agreement: 'An amendment is filed under its agreement’s own client.',
  relationship_cycle: 'A contract never sits under, or replaces, one beneath it or the agreement it sits under.',
  has_amendments: 'It has amendments of its own; move them to the agreement first.',
  has_children: 'Contracts sit under it; move them first.',
  current_base_contract: 'It is the current base; replace the base on the Base MSA page first.',
  agreement_restricted_contract_read:
    'The agreement is restricted from AI processing and the AI has already read this contract — whether it may be linked is Legal’s call.',
  pipeline_run_active: 'A pipeline run is in progress and may already have picked this contract; save the link once it ends.',
  links_changed: 'The links changed since this opened — reloaded; nothing was written.',
  lineage_outside_scope: 'A contract you cannot see sits beneath this one; ask an administrator who can see every contract involved.',
});

const plural = (count: number, one: string, many: string): string => (count === 1 ? one : many);

/** One effect as a sentence (R-120-19) — what the server says the link will change. */
function EffectSentence({ effect, target }: { readonly effect: ContractLinkEffect; readonly target: ContractLinkCandidate }) {
  switch (effect.kind) {
    case 'type_follows_agreement':
      return (
        <>
          Its type becomes <ContractTypeTag type={effect.to.contract_type} confirmation={effect.to.contract_type_confirmation} />,
          following the agreement.
        </>
      );
    case 'sow_number_taken':
      return (
        <>
          It takes the agreement’s SOW number <code>{effect.sow_number}</code>.
        </>
      );
    case 'sow_number_cleared':
      return (
        <>
          Its SOW number <code>{effect.sow_number}</code> is removed: an amendment of a {target.contract_type} carries none.
          SOW reconciliation may then report <code>{effect.sow_number}</code> as missing from CLM.
        </>
      );
    case 'parent_cleared':
      return (
        <>
          Its parent MSA link to <code>{effect.contract_key}</code> is removed: an amendment sits where its agreement sits.
        </>
      );
    case 'copies_from_agreement':
      return (
        <>
          It takes the agreement’s owner, classification and category, and{' '}
          {effect.parties_to_copy.length === 0
            ? 'no other party'
            : `${String(effect.parties_to_copy.length)} other ${plural(effect.parties_to_copy.length, 'party', 'parties')} (${effect.parties_to_copy.join(', ')})`}
          .
        </>
      );
    case 'restriction_applied':
      return (
        <>
          It becomes restricted from AI processing, as the agreement is. Save this only when no pipeline run is in
          progress, including one started from GitHub.
        </>
      );
    case 'parties_kept':
      return (
        <>
          It keeps its {plural(effect.party_keys.length, 'party', 'parties')} {effect.party_keys.join(', ')}, but they no
          longer decide who can see it: access follows the agreement.
        </>
      );
    case 'amendment_flag_cleared':
      return <>The “possibly an amendment” flag is cleared.</>;
    case 'agreement_superseded':
      return (
        <>
          <code>{effect.contract_key}</code> becomes Superseded and read-only.
        </>
      );
    case 'base_superseded':
      return (
        <>
          <code>{target.contract_key}</code> is the current base {effect.contract_type}: replace the base on the{' '}
          <Link to="/admin/base-msa">Base MSA page</Link>.
        </>
      );
    case 'stays_with_superseded':
      return (
        <>
          {effect.children.length} {plural(effect.children.length, 'contract', 'contracts')} under{' '}
          <code>{target.contract_key}</code> and {effect.amendments.length}{' '}
          {plural(effect.amendments.length, 'amendment', 'amendments')} of it stay with it.
        </>
      );
    case 'hierarchy_moved':
      return <>{effect.contract_keys.join(', ')} move with it in the hierarchy.</>;
    case 'typed_key_names_other_type':
      return <>Its ID names {effect.key_type}; IDs never change.</>;
    case 'deviation_reports_kept':
      return (
        <>
          Its {effect.count} deviation {plural(effect.count, 'report is', 'reports are')} kept; it can no longer be a
          report’s subject.
        </>
      );
  }
}

/** A key that opens its record — the record's own `404` answers for one the reader cannot see. */
function RecordKey({ contractKey }: { readonly contractKey: string }) {
  return (
    <Link to={`/contracts/record/${encodeURIComponent(contractKey)}`}>
      <code>{contractKey}</code>
    </Link>
  );
}

/** The act row's time and id — the NFR-07 receipt, as the dialog and the card both show it. */
function SavedAt({ receipt }: { readonly receipt: SetContractLinkResponse }) {
  return (
    <>
      <time dateTime={receipt.audit.occurred_at_utc}>{receipt.audit.occurred_at_utc.slice(0, 16).replace('T', ' ')}</time> UTC
      (audit {receipt.audit.audit_id})
    </>
  );
}

function LinkRow({ label, keys, testId }: { readonly label: string; readonly keys: readonly string[]; readonly testId: string }) {
  return (
    <li data-testid={testId}>
      <strong>{label}:</strong>{' '}
      {keys.length === 0 ? (
        <span className="mut">none</span>
      ) : (
        keys.map((key, index) => (
          <span key={key}>
            {index > 0 ? ', ' : null}
            <RecordKey contractKey={key} />
          </span>
        ))
      )}
    </li>
  );
}

/**
 * CLM-120 (R-120-19) — the contract's **Links**, on its record.
 *
 * Every reader sees the five links from the record's own `lineage` block —
 * parent MSA, amends, replaces, replaced by, amended by — each a key that opens
 * its record, or "none" (the tags line's word: the lineage is read, so "none" is
 * a fact here, not an unbuilt section). The record already scopes the rows
 * pointing at the contract (R-120-21); a parent or a replaced contract the
 * reader cannot see is the contract's own column and opens to the record's
 * uniform `404`.
 *
 * An administrator (`mayAttempt(identity, ['admin'])`, usability only — the
 * API's `admin:configure` is the gate) also sees **Change links…**, the dialog
 * below. Hidden on a superseded record: Rule 3 keeps its lineage history. The
 * last link saved from this record is the page's to keep across the reload it
 * causes (the type decision's `receipt` idiom), and the card shows it.
 */
export function ContractLinksPanel({
  contract,
  onChanged,
  receipt = null,
  openOn = null,
  onClosed,
}: {
  readonly contract: ContractDetail;
  /** Called with the act's response after a link is saved; the page keeps it and re-reads the record. */
  readonly onChanged: (response: SetContractLinkResponse) => void;
  /** The last link saved from this record, kept by the page across its reload. */
  readonly receipt?: SetContractLinkResponse | null;
  /** Open the dialog on this action — the type line's *Link as an amendment…* (R-120-13). */
  readonly openOn?: 'amends' | null;
  /** The dialog closed; the page clears `openOn`. */
  readonly onClosed?: () => void;
}) {
  const { identity } = useIdentity();
  const mayEdit = mayAttempt(identity, LINK_EDITOR_ROLES) && !contract.is_read_only;
  const lineage = contract.lineage;
  const [open, setOpen] = useState<{ readonly initial: LinkAction | null } | null>(null);

  useEffect(() => {
    if (openOn === 'amends' && mayEdit) setOpen({ initial: 'amends' });
  }, [openOn, mayEdit]);

  function close() {
    setOpen(null);
    onClosed?.();
  }

  return (
    <div data-testid="contract-links">
      <p className="mut">
        <strong>Links</strong>
      </p>
      <ul className="mut" aria-label="Links">
        <LinkRow label="Parent MSA" keys={lineage.parent_contract_key === null ? [] : [lineage.parent_contract_key]} testId="contract-links-parent" />
        <LinkRow label="Amends" keys={lineage.amends_contract_key === null ? [] : [lineage.amends_contract_key]} testId="contract-links-amends" />
        <LinkRow label="Replaces" keys={lineage.supersedes_contract_key === null ? [] : [lineage.supersedes_contract_key]} testId="contract-links-replaces" />
        <LinkRow
          label="Replaced by"
          keys={lineage.superseded_by_contract_key === null ? [] : [lineage.superseded_by_contract_key]}
          testId="contract-links-replaced-by"
        />
        <LinkRow label="Amended by" keys={lineage.amended_by_contract_keys} testId="contract-links-amended-by" />
      </ul>
      {receipt === null || open !== null || receipt.contract_key !== contract.contract_key ? null : (
        <p className="mut" role="status" data-testid="contract-links-receipt">
          Link saved at <SavedAt receipt={receipt} />.
        </p>
      )}
      {mayEdit && open === null ? (
        <button type="button" className="btn sm qt" onClick={() => setOpen({ initial: null })}>
          Change links…
        </button>
      ) : null}
      {open === null ? null : (
        /* Keyed on the intent: the banner's "Link as an amendment…" while the dialog is open starts it afresh on amends. */
        <ContractLinksDialog
          key={open.initial ?? 'any'}
          contractKey={contract.contract_key}
          initial={open.initial}
          onChanged={onChanged}
          onClose={close}
        />
      )}
    </div>
  );
}

type Phase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly response: ContractLinksResponse }
  | { readonly kind: 'problem'; readonly problem: ApiProblemKind; readonly correlation_id: string | null };

interface Problem {
  readonly kind: ApiProblemKind;
  readonly code: string | null;
  readonly correlation_id: string | null;
}

function problemOf(error: unknown): Problem {
  return {
    kind: classifyApiError(error),
    code: refusalCodeOf(error),
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

const isRefusalCode = (code: string | null): code is ContractLinkRefusal => code !== null && code in LINK_REFUSAL_COPY;

/**
 * The dialog (R-120-19), backed by the GET. It offers the actions the server
 * says are available — a withheld one disabled, with its reason — then the
 * candidates offering the chosen link (the upload's list; *Find by ID* past its
 * cap), the chosen one's effects as sentences, and an optional note. It posts
 * `seen` as the GET returned it, plus the chosen candidate's. It computes no rule
 * and compares no confirmation with a literal: every type renders through
 * `ContractTypeTag`.
 */
function ContractLinksDialog({
  contractKey,
  initial,
  onChanged,
  onClose,
}: {
  readonly contractKey: string;
  readonly initial: LinkAction | null;
  readonly onChanged: (response: SetContractLinkResponse) => void;
  readonly onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [generation, setGeneration] = useState(0);
  const [action, setAction] = useState<LinkAction | null>(initial);
  const [targetKey, setTargetKey] = useState<string | null>(null);
  /* The last Find by ID: the contract the GET found by key, or none. It joins the list, never replaces it. */
  const [lookup, setLookup] = useState<{ readonly key: string; readonly candidate: ContractLinkCandidate | null } | null>(null);
  const [findKey, setFindKey] = useState('');
  const [findInvalid, setFindInvalid] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [reloaded, setReloaded] = useState(false);
  const [saved, setSaved] = useState<SetContractLinkResponse | null>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abort.current?.abort();
    abort.current = controller;
    setPhase({ kind: 'loading' });
    void (async () => {
      try {
        const response = await fetchContractLinks(contractKey, undefined, controller.signal);
        if (!controller.signal.aborted) setPhase({ kind: 'loaded', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPhase({
          kind: 'problem',
          problem: classifyApiError(error),
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
        });
      }
    })();
    return () => {
      controller.abort();
    };
  }, [contractKey, generation]);

  if (phase.kind === 'loading') {
    return (
      <div className="vform" role="status" aria-busy="true" data-testid="contract-links-dialog">
        <p>Reading the links of {contractKey}…</p>
      </div>
    );
  }
  if (phase.kind === 'problem') {
    return (
      <div className="vform" data-testid="contract-links-dialog">
        <ErrorNotice kind={phase.problem} correlation_id={phase.correlation_id} />
        <div className="vacts">
          <button type="button" className="btn qt" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const response = phase.response;
  const actions: { readonly id: LinkAction; readonly label: string }[] = [
    { id: 'child_of_set', label: response.links.parent === null ? 'Set parent MSA' : 'Change parent MSA' },
    ...(response.links.parent === null ? [] : [{ id: 'child_of_remove' as const, label: 'Remove parent MSA' }]),
    { id: 'amends', label: response.links.amends === null ? 'Record as an amendment of…' : 'Change the agreement it amends' },
    { id: 'supersedes', label: 'Record as the replacement of…' },
  ];
  const link = action === null ? null : LINK_OF[action];
  /* A found contract is listed first, once, beside the server's list — for every action, not only the one it was found under. */
  const found = lookup?.candidate ?? null;
  const pool = found === null ? response.candidates : [found, ...response.candidates.filter((entry) => entry.contract_key !== found.contract_key)];
  const offering = link === null || action === 'child_of_remove' ? [] : pool.filter((candidate) => candidate.offers.some((offer) => offer.link === link));
  const foundWithheld = found === null || link === null ? null : (found.withheld.find((entry) => entry.link === link) ?? null);
  const candidate = offering.find((entry) => entry.contract_key === targetKey) ?? null;
  const effects = candidate === null || link === null ? [] : (candidate.offers.find((offer) => offer.link === link)?.effects ?? []);
  const canSave = !saving && saved === null && (action === 'child_of_remove' || candidate !== null);

  function choose(next: LinkAction) {
    setAction(next);
    setTargetKey(null);
    setProblem(null);
    setReloaded(false);
  }

  async function find() {
    const key = findKey.trim().toUpperCase();
    if (key === '') return;
    setProblem(null);
    /* A mistyped ID is the Admin's slip, not the API's: checked here as the Base MSA page checks its key, and never sent. */
    if (!ContractKeySchema.safeParse(key).success) {
      setFindInvalid(true);
      return;
    }
    setFindInvalid(false);
    try {
      const answer = await fetchContractLinks(contractKey, key);
      /* An unseen, absent or own key is no candidate (R-120-18): the list stays, and the line below says so. */
      setLookup({ key, candidate: answer.candidates.find((entry) => entry.contract_key === key) ?? null });
      setTargetKey(null);
    } catch (error) {
      setProblem(problemOf(error));
    }
  }

  async function save() {
    if (link === null) return;
    setSaving(true);
    setProblem(null);
    setReloaded(false);
    try {
      const result = await setContractLink({
        contract_key: contractKey,
        link,
        target_contract_key: candidate?.contract_key ?? null,
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        seen: {
          ...response.seen,
          target:
            candidate === null
              ? null
              : {
                  contract_key: candidate.contract_key,
                  contract_type: candidate.contract_type,
                  contract_type_confirmation: candidate.contract_type_confirmation,
                  lifecycle_status: candidate.lifecycle_status,
                  processing_restricted: candidate.processing_restricted,
                },
        },
      });
      setSaved(result);
      onChanged(result);
    } catch (error) {
      const found = problemOf(error);
      if (found.kind === 'conflict' && (found.code === 'stale_view' || found.code === 'links_changed')) {
        /* Nothing was written: read the links again and let the Admin choose afresh. */
        setReloaded(true);
        setTargetKey(null);
        setLookup(null);
        setGeneration((n) => n + 1);
      } else {
        setProblem(found);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="vform" role="group" aria-label={`Change the links of ${contractKey}`} data-testid="contract-links-dialog">
      <h4>Change the links of {contractKey}</h4>
      {reloaded ? (
        <p className="banner up" role="status" data-testid="contract-links-reloaded">
          {LINK_REFUSAL_COPY.stale_view}
        </p>
      ) : null}

      <ul className="mut" aria-label="What can change" data-testid="contract-links-actions">
        {actions.map((entry) => {
          const availability = response.available[entry.id];
          return (
            <li key={entry.id}>
              <button
                type="button"
                className={`btn sm ${action === entry.id ? 'pri' : 'qt'}`}
                disabled={!availability.offered || saving || saved !== null}
                aria-pressed={action === entry.id}
                onClick={() => choose(entry.id)}
              >
                {entry.label}
              </button>
              {availability.refusal === null ? null : <span> — {LINK_REFUSAL_COPY[availability.refusal]}</span>}
            </li>
          );
        })}
      </ul>

      {action === 'child_of_remove' && response.links.parent !== null ? (
        <p data-testid="contract-links-remove">
          Remove the parent MSA <code>{response.links.parent.contract_key}</code>? The contract stays filed; it no longer
          sits under an MSA.
        </p>
      ) : null}

      {action === null || action === 'child_of_remove' ? null : (
        <fieldset className="field" data-testid="contract-links-candidates">
          <legend>{actions.find((entry) => entry.id === action)?.label}</legend>
          {offering.length === 0 ? (
            <p className="mut">No contract you can see offers this link.</p>
          ) : (
            offering.map((entry) => (
              <label key={entry.contract_key} className="fl">
                <input
                  type="radio"
                  name={`contract-links-target-${contractKey}`}
                  value={entry.contract_key}
                  checked={targetKey === entry.contract_key}
                  disabled={saving || saved !== null}
                  onChange={() => {
                    setTargetKey(entry.contract_key);
                    setProblem(null);
                  }}
                />{' '}
                {entry.label}
              </label>
            ))
          )}
          {response.candidates_truncated || lookup !== null ? (
            <div className="field">
              <label htmlFor={`contract-links-find-${contractKey}`}>Find by ID</label>
              <input
                id={`contract-links-find-${contractKey}`}
                type="text"
                value={findKey}
                maxLength={32}
                placeholder="e.g. SGW-MSA-0003"
                onChange={(event) => {
                  setFindKey(event.target.value);
                  setFindInvalid(false);
                }}
              />
              <button type="button" className="btn sm qt" disabled={findKey.trim() === ''} onClick={() => void find()}>
                Find
              </button>
              <p className="mut">The list shows the first 100 contracts of this client by ID; find any other by its ID.</p>
              {findInvalid ? (
                <p className="mut" role="status" data-testid="contract-links-find-note">
                  That is not a contract ID. IDs look like <code>SGW-0012</code>, or <code>SGW-MSA-0003</code> for one
                  issued before 1 Oct 2026.
                </p>
              ) : lookup === null ? null : lookup.candidate === null ? (
                <p className="mut" role="status" data-testid="contract-links-find-note">
                  No other contract you can see has the ID <code>{lookup.key}</code>; the list is unchanged.
                </p>
              ) : foundWithheld === null ? null : (
                <p className="mut" role="status" data-testid="contract-links-find-note">
                  <code>{lookup.key}</code> cannot take this link: {LINK_REFUSAL_COPY[foundWithheld.refusal]}
                </p>
              )}
            </div>
          ) : null}
        </fieldset>
      )}

      {candidate === null ? null : (
        <div data-testid="contract-links-effects">
          <p>
            <ContractTypeTag type={candidate.contract_type} confirmation={candidate.contract_type_confirmation} /> ·{' '}
            {candidate.lifecycle_status}
          </p>
          {effects.length === 0 ? (
            <p className="mut">Only the link changes.</p>
          ) : (
            <ul aria-label="What this changes">
              {effects.map((effect) => (
                <li key={effect.kind} data-effect={effect.kind}>
                  <EffectSentence effect={effect} target={candidate} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {action === null ? null : (
        <div className="field">
          <label htmlFor={`contract-links-note-${contractKey}`}>Note (optional)</label>
          <textarea
            id={`contract-links-note-${contractKey}`}
            value={note}
            maxLength={500}
            disabled={saving || saved !== null}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      )}

      <div className="vacts">
        {action === null ? null : (
          <button type="button" className="btn pri" disabled={!canSave} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save link'}
          </button>
        )}
        <button type="button" className="btn qt" disabled={saving} onClick={onClose}>
          {saved === null ? 'Cancel' : 'Close'}
        </button>
      </div>

      {saved === null ? null : (
        <p className="banner up" role="status" data-testid="contract-links-saved">
          Saved at <SavedAt receipt={saved} />.
        </p>
      )}
      {problem === null ? null : isRefusalCode(problem.code) && (problem.kind === 'conflict' || problem.kind === 'validation') ? (
        <p className="banner bad" role="status" data-testid="contract-links-refusal" data-code={problem.code}>
          {LINK_REFUSAL_COPY[problem.code]}
        </p>
      ) : (
        <ErrorNotice kind={problem.kind} correlation_id={problem.correlation_id} />
      )}
    </div>
  );
}
